import { useCallback, useState } from 'react';
import type { ChangeEvent } from 'react';
import styled, { css } from 'styled-components';

import {
  defaultThemeProp,
  Flex,
  Menu,
  menuHelpers,
  useElement,
  useI18n
} from '@pega/cosmos-react-core';
import type { MenuItemProps, PopoverProps } from '@pega/cosmos-react-core';
import MultiSelectInput from '@pega/cosmos-react-core/lib/components/ComboBox/MultiSelectInput/MultiSelectInput';
import type { MultiSelectInputProps } from '@pega/cosmos-react-core/lib/components/ComboBox/MultiSelectInput/MultiSelectInput.types';
import type { FieldType } from '@pega/cosmos-react-build';

import { mockQuery } from './mockData';
import type { ContentType } from './mockData';
import Popover from './Popover';

export interface SelectionMenuProps {
  target: NonNullable<PopoverProps['target']>;
  constraint?: {
    types?: ContentType[];
    subTypes?: FieldType[];
  };
  onCancel: () => void;
  onSubmit: (selected: NonNullable<MultiSelectInputProps['selected']>) => void;
}

const StyledSelectionMenu: typeof Menu = styled(Menu)(({ theme }) => {
  return css`
    margin-inline: calc(-2 * ${theme.base.spacing});
  `;
});

StyledSelectionMenu.defaultProps = defaultThemeProp;

const rootMenuItems: MenuItemProps[] = [
  { id: 'fields', primary: 'Fields', items: [] },
  { id: 'views', primary: 'Views', items: [] }
];

const SelectionMenu = ({ target, constraint, onCancel: onClose, onSubmit }: SelectionMenuProps) => {
  const t = useI18n();
  const [loading, setLoading] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const [inputEl, setInputEl] = useElement<HTMLElement>(null);

  const [menuItems, setMenuItems] = useState<MenuItemProps[]>(() => {
    if (constraint?.types) {
      const constrainedTypes = [];
      if (constraint.types.includes('Field')) {
        constrainedTypes.push(rootMenuItems[0]);
      }
      if (constraint.types.includes('View')) {
        constrainedTypes.push(rootMenuItems[1]);
      }

      return constrainedTypes;
    }

    return rootMenuItems;
  });

  const [selected, setSelected] = useState<NonNullable<MultiSelectInputProps['selected']>>(
    () => []
  );

  const onItemExpand = useCallback(
    async (id: MenuItemProps['id']) => {
      const expandedItem = menuHelpers.getItem(menuItems, id);
      if (!expandedItem || (expandedItem.items && expandedItem.items.length > 0)) {
        return;
      }

      setLoading(true);

      const subItems = await mockQuery({
        type: id === 'fields' ? 'Field' : 'View',
        delay: 1000
      });

      const newMenuItems = menuHelpers.mapItem(menuItems, id, item => {
        return {
          ...item,
          items: subItems.map(subItem => ({
            id: subItem.id,
            primary: subItem.label,
            selected: !!selected.find(s => s.id === subItem.id)
          }))
        };
      });

      setMenuItems(newMenuItems as MenuItemProps[]);
      setLoading(false);
    },
    [menuItems, selected]
  );

  const onFilter = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.currentTarget.value;
    setFilterValue(val);

    if (val) {
      setLoading(true);
      setMenuItems([]);

      const matchedItems = await mockQuery({
        filter: val,
        delay: 1000
      });

      setMenuItems(
        matchedItems.map(matchedItem => {
          return {
            id: matchedItem.id,
            primary: matchedItem.label,
            secondary: [matchedItem.subType ?? matchedItem.type],
            selected: !!selected.find(s => s.id === matchedItem.id)
          };
        })
      );

      setLoading(false);
      return;
    }

    setMenuItems(rootMenuItems);
  }, []);

  const onItemClick = useCallback(
    (id: MenuItemProps['id']) => {
      setSelected(cur => {
        if (cur.find(s => s.id === id)) {
          return cur.filter(sel => sel.id !== id);
        }

        return [
          ...cur,
          { id, text: (menuHelpers.getItem(menuItems, id) as MenuItemProps).primary }
        ];
      });

      setMenuItems(cur => menuHelpers.toggleSelected(cur, id) as MenuItemProps[]);
    },
    [menuItems]
  );

  return (
    <Popover
      target={target}
      heading='Content selection'
      onCancel={onClose}
      onSubmit={() => {
        onSubmit(selected);
      }}
    >
      <Flex container={{ direction: 'column', gap: 2, pad: [1, 0] }}>
        <MultiSelectInput
          ref={setInputEl}
          placeholder={t('search_placeholder_default')}
          value={filterValue}
          selected={selected}
          onChange={onFilter}
          onRemove={onItemClick}
        />
        <StyledSelectionMenu
          focusControlEl={inputEl ?? undefined}
          mode='multi-select'
          items={menuItems}
          scrollAt={10}
          loading={loading}
          onItemClick={onItemClick}
          onItemExpand={onItemExpand}
        />
      </Flex>
    </Popover>
  );
};

export default SelectionMenu;
